import { useState } from "react";
import type { Candidate } from "../lib/types";

interface Props {
  candidates: Candidate[];
  selected: Set<number>;
  onToggle: (index: number) => void;
  onEdit: (index: number, patch: Partial<Candidate>) => void;
}

/**
 * The approval list. Everything arrives selected, because rejecting a few duds
 * is faster than picking twenty keepers out of a list — and the speed of this
 * screen is what decides whether the app gets used daily.
 */
export default function CandidateList({ candidates, selected, onToggle, onEdit }: Props) {
  const [editing, setEditing] = useState<number | null>(null);

  return (
    <div className="stack">
      {candidates.map((candidate, index) => {
        const isSelected = selected.has(index);
        const isEditing = editing === index;
        return (
          <div key={index} className={`candidate${isSelected ? "" : " rejected"}`}>
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => onToggle(index)}
              aria-label={`Keep ${candidate.term}`}
            />
            <div className="body">
              {isEditing ? (
                <div className="stack">
                  <input
                    type="text"
                    value={candidate.term}
                    onChange={(e) => onEdit(index, { term: e.target.value })}
                    aria-label="Term"
                  />
                  <input
                    type="text"
                    value={candidate.translation}
                    onChange={(e) => onEdit(index, { translation: e.target.value })}
                    aria-label="Translation"
                  />
                  <input
                    type="text"
                    value={candidate.example ?? ""}
                    placeholder="Example sentence"
                    onChange={(e) => onEdit(index, { example: e.target.value || null })}
                    aria-label="Example"
                  />
                  <div className="row">
                    <select
                      value={candidate.domain}
                      onChange={(e) => onEdit(index, { domain: e.target.value as Candidate["domain"] })}
                    >
                      <option value="general">General</option>
                      <option value="mining">Mining</option>
                    </select>
                    <button className="btn small" onClick={() => setEditing(null)}>Done</button>
                  </div>
                </div>
              ) : (
                <div onClick={() => setEditing(index)} style={{ cursor: "text" }}>
                  {candidate.kind === "cloze" ? (
                    <>
                      <div className="term">{candidate.cloze_text?.replace(/\{\{(.*?)\}\}/, "____")}</div>
                      <div className="translation">{candidate.cloze_answer}</div>
                    </>
                  ) : (
                    <>
                      <div className="term">{candidate.term}</div>
                      <div className="translation">{candidate.translation}</div>
                    </>
                  )}
                  {candidate.example && <div className="example">{candidate.example}</div>}
                  {candidate.note && <div className="usage">{candidate.note}</div>}
                  <div className="tags">
                    {candidate.kind === "cloze" && <span className="tag cloze">grammar</span>}
                    {candidate.kind === "phrase" && <span className="tag">phrase</span>}
                    {candidate.domain === "mining" && <span className="tag mining">mining</span>}
                    {candidate.gender && <span className="tag">{candidate.gender === "m" ? "masc" : "fem"}</span>}
                    {candidate.level && <span className="tag">{candidate.level}</span>}
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
